const toId = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const createSelectionSets = ({ pages = [], sections = [], questions = [] }) => {
  return {
    pageIdSet: new Set(pages.map((page) => toId(page?.id)).filter(Boolean)),
    sectionIdSet: new Set(
      sections.map((section) => toId(section?.id)).filter(Boolean),
    ),
    questionIdSet: new Set(
      questions.map((question) => toId(question?.id)).filter(Boolean),
    ),
  };
};

const isQuestionCovered = ({ question, sectionById, selectionSets }) => {
  if (!question) return false;

  const section = sectionById.get(toId(question.surveySectionId));
  const pageId = section ? toId(section.surveyPageId) : "";

  if (pageId && selectionSets.pageIdSet.has(pageId)) return true;
  if (selectionSets.sectionIdSet.has(toId(question.surveySectionId)))
    return true;
  if (selectionSets.questionIdSet.has(toId(question.id))) return true;

  return false;
};

export const includeControllerQuestions = ({
  selectedSurvey,
  selectedPages = [],
  selectedSections = [],
  selectedQuestions = [],
}) => {
  const pagesToCopy = [...selectedPages];
  const sectionsToCopy = [...selectedSections];
  const questionsToCopy = [...selectedQuestions];

  if (!selectedSurvey) {
    return {
      pagesToCopy,
      sectionsToCopy,
      questionsToCopy,
      autoAddedControllers: 0,
      autoAddedControllerNames: [],
    };
  }

  const sectionById = new Map(
    (selectedSurvey.sections || []).map((section) => [
      toId(section.id),
      section,
    ]),
  );
  const questionById = new Map(
    (selectedSurvey.questions || []).map((question) => [
      toId(question.id),
      question,
    ]),
  );

  const selectionSets = createSelectionSets({
    pages: pagesToCopy,
    sections: sectionsToCopy,
    questions: questionsToCopy,
  });

  const coveredQuestions = (selectedSurvey.questions || []).filter((question) =>
    isQuestionCovered({ question, sectionById, selectionSets }),
  );

  const queue = [...coveredQuestions];
  const queuedIds = new Set(queue.map((question) => toId(question.id)));
  const autoAddedControllerNames = [];
  const autoAddedControllerIds = new Set();
  let autoAddedControllers = 0;

  while (queue.length > 0) {
    const question = queue.pop();
    const controllerQuestionId = toId(question?.controlledBy?.questionId);

    if (!controllerQuestionId) {
      continue;
    }

    const controller = questionById.get(controllerQuestionId);
    if (!controller) {
      continue;
    }

    const alreadyCovered = isQuestionCovered({
      question: controller,
      sectionById,
      selectionSets,
    });

    if (!alreadyCovered) {
      questionsToCopy.push(controller);
      selectionSets.questionIdSet.add(toId(controller.id));
      autoAddedControllers++;

      const controllerId = toId(controller.id);
      if (!autoAddedControllerIds.has(controllerId)) {
        autoAddedControllerIds.add(controllerId);
        autoAddedControllerNames.push(
          String(controller.name || controller.name_fr || controllerId),
        );
      }
    }

    const controllerId = toId(controller.id);
    if (!queuedIds.has(controllerId)) {
      queuedIds.add(controllerId);
      queue.push(controller);
    }
  }

  return {
    pagesToCopy,
    sectionsToCopy,
    questionsToCopy,
    autoAddedControllers,
    autoAddedControllerNames,
  };
};
